import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Box, Divider, Typography} from '@mui/material';
import {ReactComponent as SearchIcon} from '../../../../assets/images/icon-search.svg';
import {ReactComponent as ThumbnailIcon} from '../../../../assets/images/thumbnail-icon.svg';
import {ReactComponent as PlusIcon} from '../../../../assets/images/plus-icon.svg';
import {ReactComponent as MinusIcon} from '../../../../assets/images/minus-icon.svg';
import './MainPanelToolbar.scss';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {ReactComponent as ChevronIcon} from '../../../../assets/images/chevron-base-icon.svg';
import {ReactComponent as StartEndIcon} from '../../../../assets/images/go-first-last-base-icon.svg';
import {t} from 'i18next';
import PageControllerToolbar from './PageControllerToolbar';
import {MouseToolType, truncateToFiveDecimals} from '../../docViewerUtils';
import {PageData} from '../../panels/document-center-panel/DocumentCenterPanel';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {ViewerControlTextSelectedEvent} from '../../../../shared/interfaces';
import SearchPopper from '../../components/search-popper/SearchPopper';
const DEFAULT_ZOOM = 100;
const ZOOM_CHANGE_DELTA = 25;
const HUNDRED = 100;
export enum ToolbarActionType {
  ZoomIn = 'zoomIn',
  ZoomOut = 'zoomOut',
  Thumbnail = 'thumbnail',
  Search = 'search',
  Pagination = 'pagination',
  RotateLeft = 'rotateLeft',
  RotateRight = 'rotateRight',
  CopyText = 'copyText',
  Redact = 'redact',
  Annotate = 'annotate',
  PersistetHighlight = 'persistent-highlight',
  Remark = 'remark',
}

export interface ToolbarAction {
  toolbarActionType: string;
  value?: string | number | undefined;
}

interface MainPanelToolbarProps {
  onToolbarActionChange?: (arg0: ToolbarAction) => void;
  isResponsiveMode: boolean;
  viewerControl: ViewerControl | null;
  initialScaleFactor: number;
  isReady: boolean;
  pageData: PageData;
  textSelection?: ViewerControlTextSelectedEvent;
  isImagesTab?: boolean;
  isTranslationTab?: boolean;
  tabValue: number;
  searchFn?:
    | ((
        vc: ViewerControl,
        searchTerm: string,
        isPersistentSearch: boolean
      ) => void)
    | null;
  manualSearchResults?: SearchResult[];
  searchLoading: boolean;
  disableSearch: boolean;
  initialActiveButton?: string | null;
  hasRnfvFiles: boolean;
}

const MainPanelToolbar: React.FC<MainPanelToolbarProps> = ({
  onToolbarActionChange,
  isResponsiveMode = false,
  tabValue,
  viewerControl,
  isImagesTab,
  isTranslationTab,
  initialScaleFactor,
  isReady,
  pageData,
  textSelection,
  searchFn,
  manualSearchResults,
  searchLoading = false,
  disableSearch,
  initialActiveButton,
  hasRnfvFiles,
}) => {
  const [thumbnailActive, setThumbnailActive] = useState<boolean>(false);
  const [activeButton, setActiveButton] = React.useState<string | null>(
    initialActiveButton ?? null
  );
  const [anchorEl, setAnchorEl] = useState<null | EventTarget>(null);
  const [currentSearchItem, setCurrentSearchItem] = useState<number>(-1);
  const activeDoc = useSelector((state: RootState) => state.docView.activeDoc);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchButtonResModeRef = useRef<HTMLButtonElement | null>(null);
  const wasSearchOpenRef = useRef(false);

  useEffect(() => {
    setAnchorEl(null);
    setCurrentSearchItem(-1);
  }, [tabValue]);

  useEffect(() => {
    if (
      currentSearchItem >= 0 &&
      viewerControl &&
      manualSearchResults !== undefined &&
      isReady
    ) {
      viewerControl?._cs &&
        viewerControl.setSelectedSearchResult(
          manualSearchResults[currentSearchItem],
          true
        );
    }
  }, [manualSearchResults, currentSearchItem, viewerControl, isReady]);

  const handleToolbarActionChange = (arg0: ToolbarAction) => {
    // Toggle pressed state button on button click except Search Button
    if (arg0.toolbarActionType == activeButton) {
      setActiveButton(null);
      onToolbarActionChange?.({
        toolbarActionType: '',
      });
      viewerControl?._cs &&
        viewerControl?.setCurrentMouseTool(MouseToolType.PanAndEdit);
    } else {
      setActiveButton(arg0.toolbarActionType);
      onToolbarActionChange?.(arg0);
    }
  };

  const handleThumbnailPress = useCallback(() => {
    const thumbnailDiv = document.getElementById('thumbnails');
    const resizerDiv = document.getElementById('prizmdoc-resizer');

    if (thumbnailDiv && resizerDiv) {
      if (thumbnailDiv.style.display === 'none') {
        thumbnailDiv.style.display = 'block';
        resizerDiv.style.display = 'flex';
      } else {
        thumbnailDiv.style.display = 'none';
        resizerDiv.style.display = 'none';
      }
    }
    window.dispatchEvent(new Event('resize'));
    viewerControl &&
      viewerControl?._cs &&
      viewerControl?.fitContent(window.PCCViewer.FitType.FullWidth);
    setThumbnailActive((prev) => !prev);
  }, [handleToolbarActionChange]);

  const ZoomControls = useCallback(
    ({
      viewerControl,
      isReady,
    }: {
      viewerControl: ViewerControl;
      isReady: boolean;
    }) => {
      const [value, setValue] = useState(DEFAULT_ZOOM);
      const minValue =
        isReady && viewerControl?._cs
          ? viewerControl?.getMinScaleFactor()
          : 0.5;
      const maxValue =
        isReady && viewerControl?._cs ? viewerControl?.getMaxScaleFactor() : 1;
      const scaleValue = (value / HUNDRED) * initialScaleFactor;
      const getPercentFromScale = (scale: number) =>
        (HUNDRED / initialScaleFactor) * scale;

      const getScaleFromPercent = (percent: number) =>
        (percent / HUNDRED) * initialScaleFactor;

      return (
        <>
          <ArrIconButton
            shape="none"
            size="small"
            icon={<MinusIcon />}
            ariaLabel={'Zoom Out'}
            tooltipContent="Zoom Out"
            disabled={
              truncateToFiveDecimals(scaleValue) <=
              truncateToFiveDecimals(minValue)
            }
            testId="zoom-out-doc"
            tooltipFocusableWrapper={true}
            onClick={() => {
              //Previous multiple of 25
              const newZoom =
                Math.ceil((value - ZOOM_CHANGE_DELTA) / ZOOM_CHANGE_DELTA) *
                ZOOM_CHANGE_DELTA;

              // new actual Zoom Level
              const newScaleValue = Math.max(
                getScaleFromPercent(newZoom),
                minValue
              );

              viewerControl?._cs && viewerControl.setScaleFactor(newScaleValue);
              //New actual Percent
              setValue(
                newScaleValue === minValue
                  ? getPercentFromScale(newScaleValue)
                  : newZoom
              );
            }}
          />
          <ArrTextField
            id="enabled-default-small"
            sizeVariant="small"
            size="small"
            dataTestId="main-panel-zoom-textfield"
            className="panel-zoom-field"
            aria-label="Zoom"
            value={`${Math.trunc(value)}%`}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/[^0-9]/g, '');
              setValue(Number(onlyNums));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                let restrictedScaleValue = getScaleFromPercent(value);
                if (restrictedScaleValue < minValue) {
                  restrictedScaleValue = minValue;
                } else if (restrictedScaleValue > maxValue) {
                  restrictedScaleValue = maxValue;
                }
                viewerControl?._cs &&
                  viewerControl.setScaleFactor(restrictedScaleValue);
                if (
                  restrictedScaleValue === minValue ||
                  restrictedScaleValue === maxValue
                ) {
                  setValue(getPercentFromScale(restrictedScaleValue));
                }
              }
            }}
            slotProps={{inputLabel: {shrink: false}}}
          />
          <ArrIconButton
            shape="none"
            size="small"
            icon={<PlusIcon />}
            ariaLabel={'Zoom In'}
            tooltipContent="Zoom In"
            testId="zoom-in-doc"
            tooltipFocusableWrapper={true}
            disabled={
              truncateToFiveDecimals(scaleValue) >=
              truncateToFiveDecimals(maxValue)
            }
            onClick={() => {
              //Next multiple of 25
              const newZoom =
                Math.floor((value + ZOOM_CHANGE_DELTA) / ZOOM_CHANGE_DELTA) *
                ZOOM_CHANGE_DELTA;
              // new actual Zoom Level
              const newScaleValue = Math.min(
                getScaleFromPercent(newZoom),
                maxValue
              );
              viewerControl?._cs && viewerControl.setScaleFactor(newScaleValue);

              //New actual Percent
              setValue(
                newScaleValue === maxValue
                  ? getPercentFromScale(newScaleValue)
                  : newZoom
              );
            }}
          />
        </>
      );
    },
    [viewerControl, isReady]
  );
  const PageNavigation = ({
    viewerControl,
    totalPages,
    pageNumber,
  }: {
    viewerControl: ViewerControl;
    totalPages: number;
    pageNumber: number;
  }) => {
    const [value, setValue] = useState(pageNumber);

    useEffect(() => {
      setValue(pageNumber);
    }, [pageNumber]);

    const previousDisabled = useMemo(() => pageNumber === 1, [pageNumber]);
    const nextDisabled = useMemo(
      () => pageNumber === totalPages,
      [totalPages, pageNumber]
    );

    return (
      <Box className="row-container">
        <Box className="small-gap-wrapper">
          <ArrIconButton
            testId="doc-paginate-start-button-doc"
            className="start-button"
            size="small"
            icon={<StartEndIcon className="start-icon" />}
            ariaLabel={t('docView.docPagination.start')}
            onClick={() =>
              viewerControl?._cs && viewerControl?.changeToFirstPage()
            }
            disabled={previousDisabled}
            shape={previousDisabled ? 'none' : 'default'}
            tooltipFocusableWrapper={true}
          />
          <ArrIconButton
            testId="doc-paginate-previous-button"
            className="previous-button"
            size="small"
            icon={<ChevronIcon className="previous-icon" />}
            ariaLabel={t('docView.docPagination.previous')}
            onClick={() =>
              viewerControl?._cs && viewerControl?.changeToPrevPage()
            }
            disabled={previousDisabled}
            shape={previousDisabled ? 'none' : 'default'}
            tooltipFocusableWrapper={true}
          />
        </Box>
        <Box className="input-container">
          <ArrTextField
            id="enabled-default-small"
            size="small"
            sizeVariant="small"
            dataTestId="main-panel-page-textfield"
            className="input-field"
            aria-label="Change Page"
            value={value}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/[^0-9]/g, '');
              setValue(Number(onlyNums));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                viewerControl?._cs && viewerControl?.setPageNumber(value);
              }
            }}
            slotProps={{inputLabel: {shrink: false}}}
          />
        </Box>
        <Typography
          className="typo-text body4_size dark-text"
          data-testid="total-page-count"
        >
          {`of ${totalPages} pages`}
        </Typography>
        <Box className="small-gap-wrapper">
          <ArrIconButton
            testId="doc-paginate-next-button"
            className="next-button"
            size="small"
            icon={<ChevronIcon className="next-icon" />}
            tooltipFocusableWrapper={true}
            ariaLabel={t('docView.docPagination.next')}
            onClick={() =>
              viewerControl?._cs && viewerControl?.changeToNextPage()
            }
            disabled={nextDisabled}
            shape={value >= totalPages ? 'none' : 'default'}
          />
          <ArrIconButton
            testId="doc-paginate-end-button"
            className="end-button"
            size="small"
            icon={<StartEndIcon className="end-icon" />}
            tooltipFocusableWrapper={true}
            ariaLabel={t('docView.docPagination.end')}
            onClick={() =>
              viewerControl?._cs && viewerControl?.changeToLastPage()
            }
            disabled={nextDisabled}
            shape={value >= totalPages ? 'none' : 'default'}
          />
        </Box>
      </Box>
    );
  };

  useEffect(() => {
    setActiveButton(null);
  }, [tabValue]);

  // Restore the active button when remounting (e.g. after doc navigation causes
  // the viewer to re-initialise). This runs after the [tabValue] effect (React
  // executes effects in definition order) so it wins on first mount.
  useEffect(() => {
    if (initialActiveButton) {
      setActiveButton(initialActiveButton);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // on document change thumbnail should be reset
  useEffect(() => {
    setThumbnailActive(false);
  }, [activeDoc]);

  const handleSearchClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (!anchorEl) {
      wasSearchOpenRef.current = true;
      setAnchorEl(e.target);
    }
  };

  const closeSearchMode = () => {
    setAnchorEl(null);
    setCurrentSearchItem(-1);

    if (wasSearchOpenRef.current) {
      if (viewerControl?._cs) {
        viewerControl.clearSearch();
        searchFn?.(viewerControl, '', true);
      }
      requestAnimationFrame(() => {
        if (isResponsiveMode) {
          searchButtonResModeRef.current?.focus();
        } else {
          searchButtonRef.current?.focus();
        }
        wasSearchOpenRef.current = false;
      });
    }
  };

  const searchDocument = useCallback(
    (searchText: string) => {
      if (viewerControl?._cs) {
        searchFn?.(viewerControl, searchText, false);
      }
    },
    [viewerControl]
  );

  const navigateResults = useCallback(
    (index: number) => {
      setCurrentSearchItem(index);
    },
    [setCurrentSearchItem]
  );
  return (
    <>
      <SearchPopper
        anchorEl={anchorEl as HTMLElement}
        loading={searchLoading}
        currentItem={currentSearchItem}
        totalItems={manualSearchResults?.length ?? 0}
        onClose={closeSearchMode}
        searchDocument={searchDocument}
        navigateResults={navigateResults}
        tabValue={tabValue}
      />
      <Box className="main-panel-toolbar">
        <Box className="page-controls">
          {viewerControl && isReady && (
            <>
              <PageNavigation
                viewerControl={viewerControl}
                totalPages={pageData.totalPages}
                pageNumber={pageData.pageNumber}
              />
              <Divider
                orientation="vertical"
                className="panel-divider"
                flexItem
              />
              <ZoomControls viewerControl={viewerControl} isReady={isReady} />
              <Divider
                orientation="vertical"
                className="panel-divider"
                flexItem
              />
            </>
          )}
          {!isTranslationTab && !isImagesTab && (
            <ArrIconButton
              icon={<ThumbnailIcon />}
              onClick={handleThumbnailPress}
              tooltipTrigger="hover"
              tooltipContent={t('docView.toolbar.thumbnail')}
              tooltipFocusableWrapper={true}
              ariaLabel="thumbnail"
              shape="none"
              size="small"
              testId="thumbnail-button"
              className={`thumbnail-icon action-icon ${thumbnailActive ? 'pressed' : ''}`}
            />
          )}
          {!isTranslationTab && !isImagesTab && (
            <Divider
              orientation="vertical"
              className="panel-divider"
              flexItem
            />
          )}
          {((!isResponsiveMode || isTranslationTab || isImagesTab) && (
            <PageControllerToolbar
              activeButton={activeButton}
              handleToolbarActionChange={handleToolbarActionChange}
              viewerControl={viewerControl}
              isViewerReady={isReady}
              textSelection={textSelection}
              isTranslations={isTranslationTab}
              isImagesTab={isImagesTab}
              hasRnfvFiles={hasRnfvFiles}
            />
          )) || (
            <ArrIconButton
              ref={searchButtonResModeRef}
              icon={<SearchIcon />}
              onClick={handleSearchClick}
              tooltipFocusableWrapper={true}
              disabled={disableSearch}
              ariaLabel="search"
              shape="none"
              size="small"
              testId="search-button-doc"
              className={`search-icon action-icon ${activeButton == ToolbarActionType.Search ? 'pressed' : ''}`}
            />
          )}
        </Box>
        {!isResponsiveMode && (
          <Box className="search-controls">
            <ArrIconButton
              ref={searchButtonRef}
              icon={<SearchIcon />}
              onClick={handleSearchClick}
              tooltipFocusableWrapper={true}
              disabled={disableSearch}
              ariaLabel="search"
              shape="none"
              size="small"
              testId="search-button"
              className={`search-icon action-icon ${anchorEl ? 'pressed' : ''}`}
            />
          </Box>
        )}
      </Box>
      {isResponsiveMode && !isTranslationTab && !isImagesTab && (
        <Box className="main-panel-toolbar">
          <PageControllerToolbar
            activeButton={activeButton}
            handleToolbarActionChange={handleToolbarActionChange}
            isViewerReady={isReady}
            viewerControl={viewerControl}
            textSelection={textSelection}
            hasRnfvFiles={hasRnfvFiles}
          />
        </Box>
      )}
    </>
  );
};

export default MainPanelToolbar;
